import React, { useState } from 'react';
import { motion } from 'framer-motion';
interface MoleculeInteractiveProps {
  accent: string;
}
type CellLine = 'cho' | 'human';
const A23 = '#5468D8';
const A26 = '#0F9C8C';

// Glycan branches: [x1,y1,x2,y2] with a terminal cap at (x2,y2). `mixed` caps become α2,6 in human cell line.
const BRANCHES: {
  d: string;
  cx: number;
  cy: number;
  mixed: boolean;
}[] = [{
  d: 'M150 90 L120 50',
  cx: 120,
  cy: 50,
  mixed: true
}, {
  d: 'M150 90 L170 40',
  cx: 170,
  cy: 40,
  mixed: false
}, {
  d: 'M230 100 L260 55',
  cx: 260,
  cy: 55,
  mixed: true
}, {
  d: 'M230 100 L215 50',
  cx: 215,
  cy: 50,
  mixed: false
}, {
  d: 'M140 170 L100 205',
  cx: 100,
  cy: 205,
  mixed: false
}, {
  d: 'M240 165 L285 200',
  cx: 285,
  cy: 200,
  mixed: true
}, {
  d: 'M240 165 L250 215',
  cx: 250,
  cy: 215,
  mixed: false
}];
export function MoleculeInteractive({
  accent
}: MoleculeInteractiveProps) {
  const [line, setLine] = useState<CellLine>('human');
  return <div className="grid grid-cols-[1.1fr_1fr] items-center gap-6" data-id="element-189" data-mp="2jqxtpo8c1xcl">
      <div className="rounded-3xl border border-navy-900/[0.08] bg-white p-4" data-id="element-190" data-mp="1qx4svx7jguoy">
        <svg viewBox="0 0 380 250" className="w-full" role="img" aria-label="Schematic of FSH glycosylation" data-id="element-191" data-mp="1mxm7z1fpwjm4">
          <ellipse cx="170" cy="130" rx="70" ry="48" fill="#E6EAFB" data-id="element-192" data-mp="20vkk006kg3yg" />
          <ellipse cx="225" cy="135" rx="62" ry="44" fill="#D9DFF8" data-id="element-193" data-mp="1vxsjihruv9in" />
          <text x="150" y="134" fontSize="11" fill="#4A6489" data-id="element-194" data-mp="1vvg1k4m3kz5l">α</text>
          <text x="240" y="139" fontSize="11" fill="#4A6489" data-id="element-195" data-mp="188q4zisipwsd">β</text>
          {BRANCHES.map((b, i) => {
          const isA26 = line === 'human' && b.mixed;
          return <g key={i} data-id="element-196" data-mp="1hyt5e41yyd0q" data-mpi={i}>
                <path d={b.d} stroke="#9AA9C2" strokeWidth="2" data-id="element-197" data-mp="1hzcf4xhgtv3o" />
                <motion.circle cx={b.cx} cy={b.cy} r="8" animate={{
              fill: isA26 ? A26 : A23,
              scale: isA26 ? [1, 1.3, 1] : 1
            }} transition={{
              duration: 0.5,
              delay: i * 0.05
            }} data-id="element-198" data-mp="1d6rjdqf017xi" />
              </g>;
        })}
        </svg>
        <div className="flex justify-center gap-5 text-[11px] text-slate-500" data-id="element-199" data-mp="2kkd6xf5g8f1b">
          <span className="flex items-center gap-1.5" data-id="element-200" data-mp="2pieyypkuroox">
            <span className="h-2.5 w-2.5 rounded-full" style={{
            backgroundColor: A23
          }} data-id="element-201" data-mp="2uaomjyyhwco1" /> α2,3-linked sialic acid
          </span>
          <span className="flex items-center gap-1.5" data-id="element-202" data-mp="2yaypf2qzqmyo">
            <span className="h-2.5 w-2.5 rounded-full" style={{
            backgroundColor: A26
          }} data-id="element-203" data-mp="21lr3htmgmbu8" /> α2,6-linked sialic acid
          </span>
        </div>
      </div>

      <div data-id="element-204" data-mp="26ln91xat2bbw">
        <div className="grid grid-cols-2 gap-1 rounded-full bg-mist-200 p-1" role="radiogroup" aria-label="Cell line" data-id="element-205" data-mp="26l41nais75c4">
          {[{
          id: 'cho' as const,
          label: 'CHO cell line'
        }, {
          id: 'human' as const,
          label: 'Human cell line'
        }].map(o => <button key={o.id} role="radio" aria-checked={line === o.id} onClick={() => setLine(o.id)} className={`rounded-full px-3 py-2 text-[13px] font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris ${line === o.id ? 'bg-white text-navy-900 shadow-sm' : 'text-slate-500'}`} data-id="element-206" data-mp="3m0t3fnn1qhki">
              {o.label}
            </button>)}
        </div>
        <motion.div key={line} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mt-5" data-id="element-207" data-mp="3ncpqhx6x0obe">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em]" style={{
          color: accent
        }} data-id="element-208" data-mp="3iffs3s1jt2bf">
            {line === 'human' ? 'Human cell line (PER.C6®)' : 'Chinese hamster ovary (CHO)'}
          </p>
          <p className="mt-2 text-[15px] leading-relaxed text-navy-900" data-id="element-209" data-mp="38i1dzz33kpma">
            {line === 'human' ? 'Follitropin delta carries both α2,3- and α2,6-linked sialic acid. [Ref]' : 'CHO-derived recombinant FSH carries α2,3-linked sialic acid only. [Ref]'}
          </p>
          <p className="mt-4 text-[11px] text-slate-400" data-id="element-210" data-mp="12kvwg62v4h34">Schematic representation, not to scale.</p>
        </motion.div>
      </div>
    </div>;
}